/**
 * Security Logging Utility
 * 
 * Logs security-related events to the backend for monitoring and alerting.
 */

import { supabase } from '@/integrations/supabase/client';

export type SecurityEventType = 
  | 'admin_login'
  | 'admin_login_failed'
  | 'password_reset'
  | 'email_change'
  | 'signup'
  | 'suspicious_activity'
  | 'settings_change'
  | 'sync_trigger'
  | 'credit_adjustment';

interface LogSecurityEventOptions {
  eventType: SecurityEventType;
  success: boolean;
  email?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Log a security event to the backend
 * This calls an edge function that records the event with IP/user agent
 */
export async function logSecurityEvent(options: LogSecurityEventOptions): Promise<void> {
  const { eventType, success, email, metadata = {} } = options;

  try {
    // Get current session for auth header
    const { data: { session } } = await supabase.auth.getSession();
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    // Call the edge function to log the event
    // The edge function will capture IP and user agent from headers
    await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-security-event`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          event_type: eventType,
          success,
          email,
          metadata,
        }),
      }
    );
  } catch (error) {
    // Silently fail - we don't want logging to break the app
    console.error('Failed to log security event:', error);
  }
}

/**
 * Log an admin login attempt
 */
export async function logAdminLogin(email: string, success: boolean): Promise<void> {
  await logSecurityEvent({
    eventType: success ? 'admin_login' : 'admin_login_failed',
    success,
    email,
    metadata: {
      action: 'admin_login',
      timestamp: new Date().toISOString(),
    },
  });
}

/**
 * Log a manual sync trigger
 */
export async function logSyncTrigger(syncType: string, adminId: string): Promise<void> {
  await logSecurityEvent({
    eventType: 'sync_trigger',
    success: true,
    metadata: {
      sync_type: syncType,
      admin_id: adminId,
      timestamp: new Date().toISOString(),
    },
  });
}

/**
 * Log a settings change
 */
export async function logSettingsChange(
  settingKey: string,
  oldValue: unknown,
  newValue: unknown
): Promise<void> {
  await logSecurityEvent({
    eventType: 'settings_change',
    success: true,
    metadata: {
      setting_key: settingKey,
      old_value: oldValue,
      new_value: newValue,
      timestamp: new Date().toISOString(),
    },
  });
}

/**
 * Check if user is admin and log login accordingly
 */
export async function logLoginWithAdminCheck(
  email: string, 
  userId: string, 
  success: boolean
): Promise<void> {
  // Check if user is an admin
  const { data: roleData } = await supabase
    .from('user_roles')
    .select('role')
    .eq('user_id', userId)
    .in('role', ['admin', 'super_admin'])
    .maybeSingle();

  if (roleData) {
    // This is an admin login
    await logAdminLogin(email, success);
  }
}
